import { useEffect, useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc } from "../../ipc/IpcProvider";
import type { KpiTargetRow, PeriodRange } from "../../ipc/types";
import { useDateRange } from "../../components/DateRangeContext";
import { formatRange } from "../../lib/date-range";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";

const STATUS_TONE: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  on_track: "success", at_risk: "warning", off_track: "danger",
};

/**
 * Analytics con METAS CONFIGURABLES. Los valores actuales se derivan solos de tu operación real
 * (Ventas, Inventario, Logística, Compras, Precios); tú defines la META de cada KPI y el semáforo
 * (en meta / en riesgo / fuera de meta) pasa a reflejar TUS objetivos, no números genéricos.
 */
export function AnalyticsScreen(): JSX.Element {
  const client = useIpc();
  const [rows, setRows] = useState<readonly KpiTargetRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const { range, previous, compare } = useDateRange();
  const periodRange: PeriodRange = {
    fromEpoch: range.fromEpoch, toEpoch: range.toEpoch,
    prevFromEpoch: compare ? previous.fromEpoch : 0,
    prevToEpoch: compare ? previous.toEpoch : 0,
  };
  const load = async (): Promise<void> => {
    const r = await client.getAnalyticsTargets(periodRange);
    if (r.ok) { setRows(r.value.items); setError(null); }
    else setError(r.error.message);
  };
  useEffect(() => { void load(); }, [range.fromEpoch, range.toEpoch, previous.fromEpoch, previous.toEpoch, compare]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (key: string): Promise<void> => {
    const raw = drafts[key];
    const value = Number(raw);
    if (raw === undefined || !Number.isFinite(value) || value <= 0) { setMsg("Ingresa una meta válida (número mayor que 0)."); return; }
    setSavingKey(key); setMsg(null);
    const r = await client.setAnalyticsTarget(key, value);
    setSavingKey(null);
    if (!r.ok) { setMsg(r.error.message); return; }
    setRows(r.value.items);
    setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
    setMsg("Meta actualizada.");
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (rows === null) return <LoadingState />;

  const onTrack = rows.filter((k) => k.status === "on_track").length;
  const atRisk = rows.filter((k) => k.status === "at_risk").length;
  const avg = rows.length === 0 ? 0 : Math.round(rows.reduce((a, k) => a + k.attainment, 0) / rows.length);

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="analytics" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Analytics</h1>
              <p className="pagehead__sub">Tus KPIs se calculan solos desde tu operación real. Define tus metas y el semáforo reflejará tus objetivos.</p>
              <div className="row" style={{ gap: 8, marginTop: 4 }}>
                <Badge tone="brand" dot>{formatRange({ fromEpoch: range.fromEpoch, toEpoch: range.toEpoch })}</Badge>
                <span className="faint" style={{ fontSize: 12 }}>KPIs de período (ingreso, órdenes); stock es estado actual</span>
              </div>
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{
              title: "Analytics · KPIs y metas",
              periodLabel: formatRange({ fromEpoch: range.fromEpoch, toEpoch: range.toEpoch }),
              columns: ["KPI", "Actual", "Meta", "Cumplimiento"],
              rows: rows.map((k) => ({ primary: k.name, secondary: k.current.toLocaleString("es-CL"), status: { label: k.target.toLocaleString("es-CL") }, amount: `${k.attainment}%` })),
              metrics: [
                { label: "KPIs monitoreados", value: String(rows.length) },
                { label: "En meta", value: String(onTrack) },
                { label: "En riesgo", value: String(atRisk) },
                { label: "Cumplimiento prom.", value: `${avg}%` },
              ],
            }} />
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        <StatCard label="KPIs monitoreados" value={String(rows.length)} icon="analytics" accent="yellow" />
        <StatCard label="En meta" value={String(onTrack)} icon="check" accent="green" />
        <StatCard label="En riesgo" value={String(atRisk)} icon="pulse" accent="blue" />
        <StatCard label="Cumplimiento prom." value={`${avg}%`} icon="target" accent="red" />
      </div>

      {msg ? <div className="callout" style={{ margin: "var(--sp-4) 0" }}><div className="bar" /><div className="body">{msg}</div></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Indicadores y metas" subtitle="El valor actual es automático; edita la META de cada indicador y guarda." action={<Badge tone="brand" dot>En vivo</Badge>} />
        <div className="card__body" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>KPI</th><th>Actual</th><th>Meta</th><th>Cumplimiento</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {rows.map((k) => {
                const draft = drafts[k.key];
                const dirty = draft !== undefined && draft !== String(k.target);
                return (
                  <tr key={k.key}>
                    <td><div className="strong">{k.name}</div>{k.isCustom ? <div className="faint" style={{ fontSize: 11.5 }}>meta personalizada</div> : <div className="faint" style={{ fontSize: 11.5 }}>meta por defecto</div>}</td>
                    <td className="strong">{k.current.toLocaleString("es-CL")}</td>
                    <td>
                      <input
                        type="number" min={1} className="pd-input" style={{ width: 110, height: 32 }}
                        aria-label={`Meta de ${k.name}`}
                        value={draft ?? String(k.target)}
                        onChange={(e) => setDrafts((d) => ({ ...d, [k.key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") void save(k.key); }}
                      />
                    </td>
                    <td className="strong">{k.attainment}%</td>
                    <td><Badge tone={STATUS_TONE[k.status] ?? "neutral"} dot>{k.statusLabel}</Badge></td>
                    <td><Button size="sm" variant={dirty ? "primary" : "ghost"} disabled={!dirty || savingKey === k.key} onClick={() => save(k.key)}>{savingKey === k.key ? "Guardando…" : "Guardar"}</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
